import { useCallback, useMemo } from 'react'

import translationKeys from '@translationKeys'
import { Nullable } from '@types'

import { ShortListCustomField, ShortlistCategoriesConfigProps } from '@modules/shortlist/types'
import { useWindowResize } from '@shared/hooks/useWindowResize'

import { CompanyCell } from './columns/CompanyCell'
import { ContactStatusCell } from './columns/ContactStatusCell'
import { CreatedAtCell } from './columns/CreatedAtCell'
import { CustomColumnCell } from './columns/CustomColumnCell'
import { FutureEventsCell } from './columns/FutureEventCell'
import { LastEventCell } from './columns/LastEventCell'
import { NdaCell } from './columns/NdaCell'
import { NoteCell } from './columns/NoteCell'
import { PhaseCell } from './columns/PhaseCell'
import { PriorityCell } from './columns/PriorityCell'
import { ProfileContactCell } from './columns/ProfileContactCell'
import { ProfileTitleRowCell } from './columns/ProfileTitleCell'
import { TagIdCell } from './columns/TagIdCell'
import { UpdatedAtCell } from './columns/UpdatedAtCell'

const defaultWidth = 200
const dockPanelSize = 92

export const useTableColumns = (props: {
  optionalColumnsConfig?: Nullable<ShortlistCategoriesConfigProps>
  customFields?: ShortListCustomField[]
  isSubscriber?: boolean
  isEcommerce?: boolean
  shortlistId?: number
}) => {
  const { optionalColumnsConfig, customFields, isSubscriber, isEcommerce } = props

  const { windowWidth = window.innerWidth } = useWindowResize()

  const getColumnWidth = useCallback(
    (fieldName: string) => {
      return optionalColumnsConfig?.[fieldName]?.width ?? defaultWidth
    },
    [optionalColumnsConfig],
  )

  const customFieldsColumns = useMemo(
    () =>
      customFields?.map(field => ({
        accessorKey: field.id.toString(),
        fieldName: field.id.toString(),
        fieldType: field.fieldType,
        fieldOptions: field.fieldOptions,
        header: () => <span>Visits</span>,
        cell: CustomColumnCell,
        size: getColumnWidth(field.id.toString()),
        minSize: 150,
        columnTitle: field.fieldName,
        restCellProps: {},
        isSortable: true,
        isCustomField: true,
      })) ?? [],
    [customFields, getColumnWidth],
  )

  const titleMaxWidth = useMemo(() => {
    const actionColumnWidth = !isEcommerce ? 190 : 0
    return windowWidth - actionColumnWidth - dockPanelSize - defaultWidth
  }, [windowWidth, isEcommerce])

  const columns = useMemo(() => {
    const phaseColumn = []
    const companyColumn = []
    const actionColumn = []
    if (isSubscriber || isEcommerce) {
      phaseColumn.push({
        accessorKey: 'phase',
        fieldName: 'phase',
        header: () => <span>Visits</span>,
        cell: PhaseCell,
        size: getColumnWidth('phase'),
        minSize: 100,
        isEnabled: false,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_PHASE,
        restCellProps: { $nowrap: true },
        isSortable: true,
      })
      companyColumn.push({
        accessorKey: 'company',
        fieldName: 'company',
        cell: CompanyCell,
        size: getColumnWidth('company'),
        minSize: 100,
        isEnabled: false,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_COMPANY_NAME,
        isSortable: true,
      })
    }

    if (!isEcommerce) {
      actionColumn.push({
        accessorKey: 'profileContact',
        fieldName: 'profileContact',
        cell: ProfileContactCell,
        isEnabled: true,
        restCellProps: { $isContactCell: true, $cellWidth: 190, $fixedWidth: true },
      })
    }

    return [
      {
        accessorKey: 'profileTitle',
        fieldName: 'profileTitle',
        header: () => <span>Visits</span>,
        cell: ProfileTitleRowCell,
        size: getColumnWidth('title'),
        minSize: 300,
        maxSize: titleMaxWidth,
        isEnabled: true,
        restCellProps: { $isNameCell: true },
        isDragHandle: true,
        isSortable: true,
        customDisable: true,
      },
      ...companyColumn,
      {
        accessorKey: 'lastEvent',
        fieldName: 'lastEvent',
        header: () => <span>Visits</span>,
        cell: LastEventCell,
        size: getColumnWidth('lastEvent'),
        minSize: 120,
        isEnabled: false,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_LAST_EVENT,
        isSortable: true,
      },
      {
        accessorKey: 'futureEvents',
        fieldName: 'futureEvents',
        header: () => <span>Visits</span>,
        cell: FutureEventsCell,
        size: getColumnWidth('futureEvents'),
        minSize: 120,
        isEnabled: false,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_FUTURE_EVENTS,
        isSortable: true,
      },
      {
        accessorKey: 'contactStatus',
        fieldName: 'contactStatus',
        header: () => <span>Visits</span>,
        cell: ContactStatusCell,
        size: getColumnWidth('contactStatus'),
        minSize: 200,
        isEnabled: false,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_CONTACT_STATUS,
        restCellProps: { $nowrap: true },
        isSortable: true,
      },
      {
        accessorKey: 'updatedAt',
        fieldName: 'updatedAt',
        header: () => <span>Visits</span>,
        cell: UpdatedAtCell,
        size: getColumnWidth('updatedAt'),
        minSize: 100,
        isEnabled: false,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_UPDATED,
        restCellProps: { $nowrap: true },
        isSortable: true,
      },
      {
        accessorKey: 'createdAt',
        fieldName: 'createdAt',
        header: () => <span>Visits</span>,
        cell: CreatedAtCell,
        size: getColumnWidth('createdAt'),
        minSize: 120,
        isEnabled: false,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_DATE_ADDED,
        restCellProps: { $nowrap: true },
        isSortable: true,
      },
      {
        accessorKey: 'nda',
        fieldName: 'nda',
        header: () => <span>Visits</span>,
        cell: NdaCell,
        size: getColumnWidth('nda'),
        minSize: 94,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_NDA,
        isSortable: true,
      },
      {
        accessorKey: 'priority',
        fieldName: 'priority',
        header: () => <span>Visits</span>,
        cell: PriorityCell,
        size: getColumnWidth('priority'),
        minSize: 145,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_PRIORITY,
        isSortable: true,
      },
      {
        accessorKey: 'tagId',
        fieldName: 'tagId',
        header: () => <span>Visits</span>,
        cell: TagIdCell,
        size: getColumnWidth('tagId'),
        minSize: 200,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_CUSTOM_TAG,
        isSortable: true,
      },
      ...customFieldsColumns,
      {
        accessorKey: 'note',
        fieldName: 'note',
        header: () => <span>Visits</span>,
        cell: NoteCell,
        size: getColumnWidth('note'),
        minSize: 120,
        columnTitle: translationKeys.CUSTOM_LIST_TABLE_HEADER_NOTES,
        isSortable: true,
      },
      ...phaseColumn,
      ...actionColumn,
      // {
      //   accessorKey: 'stretchColumn',
      //   isEnabled: true,
      //   columnTitle: '',
      //   restCellProps: { $cellWidth: 1, $noPadding: true },
      // },
    ]
  }, [customFieldsColumns, getColumnWidth, titleMaxWidth, isEcommerce, isSubscriber])

  return columns
}
